import {find} from 'lodash';

import {Component, EventEmitter, Input, Output} from '@angular/core';

import {IResponseBase} from '../../../models/survey-responses/IResponseBase';
import {IMultilingualDataOption} from '../../../models/survey/ISurveyQuestion';
import {OptionModeEnum} from '../../enum/OptionMode.enum';
import {OptionType} from '../../enum/OptionType.enum';

export interface ClickHandlerResponse {
    option: IMultilingualDataOption;
    mode: OptionModeEnum;
}

@Component({
    selector: 'surveys-element-options-block',
    template: `
        <div class="options">
            <div
                class="container"
                role="group"
                [attr.aria-labelledby]="labeledby"
                [ngStyle]="customOptionContainerStyles"
            >
                <div
                    *ngFor="let option of options"
                    class="option-wrapper"
                    [class.expanded]="shouldExpand(option)"
                >
                    <r-button
                        [selected]="isOptionSelected(option.id)"
                        [classes]="['option', option.text[language].length < 15 ? 'center-text' : '', shouldExpand(option) && isOptionSelected(option.id) && option.expandType ? 'expanded-option-' + option.expandType : '']"
                        [selection]="selection"
                        [affirmative]="true"
                        [disabled]="isOptionDisabled(option.id)"
                        [e2e]="'multi-select-option-' + option.id"
                        [describedby]="describedby"
                        [styles]="customOptionStyles"
                        (onClick)="execute(option)"
                    >
                        <span class="option-text">{{ option.text[language] }}</span>
                        <svg *ngIf="shouldExpand(option) && isOptionSelected(option.id) && option.expandType === 'success'" class="expand-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM14.59 5.58L8 12.17L5.41 9.59L4 11L8 15L16 7L14.59 5.58Z"/>
                        </svg>
                        <svg *ngIf="shouldExpand(option) && isOptionSelected(option.id) && option.expandType === 'warning'" class="expand-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9 13H11V15H9V13ZM9 5H11V11H9V5ZM9.99 0C4.47 0 0 4.48 0 10C0 15.52 4.47 20 9.99 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 9.99 0ZM10 18C5.58 18 2 14.42 2 10C2 5.58 5.58 2 10 2C14.42 2 18 5.58 18 10C18 14.42 14.42 18 10 18Z"/>
                        </svg>
                        <div *ngIf="shouldExpand(option) && isOptionSelected(option.id) && option.description" class="option-description" [innerHTML]="option.description[language]">
                        </div>
                        <div *ngIf="shouldExpand(option) && isOptionSelected(option.id)" 
                                class="continue-text" 
                                [class.continue-warning]="option.expandType === 'warning'">
                            <span>Continue</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="7" height="14" viewBox="0 0 7 14" fill="none">
                                <path d="M1 12.7132L6 6.85658L1 1" [attr.stroke]="getContinueStrokeColor(option)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </div>
                    </r-button>

                    <input
                        *ngIf="option.type === 'other' && formControls[option.id]"
                        type="text"
                        class="input"
                        [formControl]="formControls[option.id]"
                    />
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./element-options-block.component.scss']
})
export class ElementOptionsBlockComponent {
    @Input()
    options: Array<IMultilingualDataOption>;

    @Input()
    responses: Array<IResponseBase>;

    @Input()
    selectedOptionId?: string;

    @Input()
    language: string;

    @Input()
    selection? = false;

    @Input()
    maxSelectionReached? = false;

    @Input()
    cssClasses?: string[];

    @Input()
    labeledby: string;

    @Input()
    describedby: string;

    @Input()
    customOptionContainerStyles?: {[key: string]: string};

    @Input()
    customOptionStyles?: {[key: string]: string};

    @Output()
    clickHandler: EventEmitter<ClickHandlerResponse> = new EventEmitter<ClickHandlerResponse>();

    public formControls = {};

    public isOptionSelected(id: string): boolean {
        // if a selectedOptionId was passed in from a parent component,
        // such as in the case of a multiple choice element, compare that
        // to the id of the option.
        if (this.selectedOptionId) {
            return this.selectedOptionId === id;
        }

        // no selectedOptionId was passed and there are no responses
        if (!this.responses) return false;

        const response = find(this.responses, ['id', id]);
        return response ? response.value === true : false;
    }

    public isOptionDisabled(id: string): boolean {
        return this.maxSelectionReached && !this.isOptionSelected(id);
    }

    public execute(option: IMultilingualDataOption) {
        if (option.type === OptionType.NONE) {
            this.handleNoneOptionTypeSelection();
        }

        this.emit({...(option as IMultilingualDataOption)}, this.getCurrentSelectionMode(option.id));
    }

    public emit(option: IMultilingualDataOption, mode: OptionModeEnum) {
        this.clickHandler.emit({option, mode});
    }

    private handleNoneOptionTypeSelection() {
        this.formControls = {};
    }

    private getCurrentSelectionMode(id: string): OptionModeEnum {
        return this.isOptionSelected(id) ? OptionModeEnum.deselect : OptionModeEnum.select;
    }

        public shouldExpand(option: IMultilingualDataOption): boolean {
        return option.enableExpand === true;
    }

    public getContinueStrokeColor(option: IMultilingualDataOption): string {
        return option.expandType === 'warning' ? '#DE899B' : '#43A047';
    }
}
